import {
  ConfigurationDto,
  postSaveConfiguration,
  PrintExampleDto2,
} from '@/api-sdk';
import {
  GlobalComponentType,
  personalisation,
  PersonalisationType,
  printExample,
} from '@/context';

/**
 * Saves the configuration and associated contexts, then executes a callback with the configuration ID.
 *
 * @param globalContext - The global context containing debtor, country, layout, and locale.
 * @param personalisationContext - Optional personalisation context array.
 * @param printExampleContext - Optional print example context.
 * @param configuration - The configuration data to be saved.
 * @param callback - The callback function to be executed with the configuration ID.
 */
export const saveConfiguration = async ({
  globalContext,
  personalisationContext,
  printExampleContext,
  configuration,
  callback,
}: {
  globalContext: GlobalComponentType;
  personalisationContext?: PersonalisationType[];
  printExampleContext?: PrintExampleDto2;
  configuration: ConfigurationDto;
  callback: (configurationId: string) => void;
}) => {
  const { debtor, country, layout, locale } = globalContext;

  const configurationId = await postSaveConfiguration({
    params: {
      debtor: layout === 'wholesale' ? debtor : undefined,
      country,
    },
    data: configuration,
  });

  if (configurationId) {
    if (personalisationContext) {
      await personalisation.save(
        personalisationContext,
        configurationId,
        locale,
      );
    }
    if (printExample) {
      await printExample.save(printExampleContext, configurationId);
    }
    callback(configurationId);
  }
};

/**
 * Returns only the newly added colors by comparing two arrays
 * @param oldColors The previous array of colors
 * @param newColors The new array of colors
 * @returns An array containing only the colors that are in newColors but not in oldColors
 */
export function getNewlyAddedColors(
  oldColors: string[],
  newColors: string[],
): string[] {
  return newColors.filter(color => !oldColors.includes(color));
}
